import React, { useEffect, useState } from 'react';

import { InfoCircleFilledIcon } from '@vlabs/icons';
import { Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { useForm } from 'react-hook-form';

import {
  GROUP_NAME_PATTERN,
  loadDepartmentOptions,
  loadPermissionTagOptions,
} from '@vlabs/monokit/entities/sources';
import type { SourceGroupOption } from '@vlabs/monokit/entities/sources';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { selectMenuPortalStyles } from '@vlabs/monokit/shared/ui/select-portal';

import { useCreateSourceGroupTranslation } from '../i18n';
import type { CreateSourceGroupFormValues } from '../types';
import st from './CreateSourceGroupForm.module.sass';

const displayName = 'CreateSourceGroupForm';
const testid = buildTestid(displayName);

type CreateSourceGroupFormFields = Pick<CreateSourceGroupFormValues, 'group_name' | 'description'>;

interface CreateSourceGroupFormProps {
  defaultValues?: Partial<CreateSourceGroupFormFields>;
  onCancel: VoidFunction;
  onSubmit: (data: CreateSourceGroupFormValues) => Promise<void> | void;
  isPending: boolean;
}

function CreateSourceGroupForm({
  defaultValues = {},
  onCancel,
  onSubmit,
  isPending,
}: CreateSourceGroupFormProps): React.JSX.Element {
  const { t } = useCreateSourceGroupTranslation();
  const [departmentOption, setDepartmentOption] = useState<SourceGroupOption | undefined>();
  const [permissionTagOption, setPermissionTagOption] = useState<SourceGroupOption | undefined>();

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<CreateSourceGroupFormFields>({
    defaultValues: { group_name: '', description: '', ...defaultValues },
  });

  useEffect(() => {
    loadDepartmentOptions().then((options) => {
      if (options.length === 1) setDepartmentOption(options[0]);
    });
  }, []);

  useEffect(() => {
    loadPermissionTagOptions().then((options) => {
      if (options.length === 1) setPermissionTagOption(options[0]);
    });
  }, []);

  const isSubmitDisabled = isPending
    || !departmentOption
    || !permissionTagOption;

  const submit = handleSubmit(async (values: CreateSourceGroupFormFields) => {
    if (!departmentOption || !permissionTagOption) return undefined;

    return onSubmit({
      group_name: values.group_name,
      description: values.description,
      owner_department: departmentOption.value,
      permission_tag: permissionTagOption.value,
    });
  });

  return (
    <form onSubmit={submit}>
      <div className={st.Root}>
        <div className={st.Content}>
          <h5 className={st.Title}>
            {t('create_source_group_form.title')}
          </h5>

          <div className={st.Field}>
            <Control.Input
              errorMessage={errors.group_name?.message}
              errors={errors}
              id="group_name"
              label={t('create_source_group_form.group_name')}
              {...register('group_name', {
                pattern: {
                  message: t('create_source_group_form.validation.name_pattern'),
                  value: GROUP_NAME_PATTERN,
                },
                required: t('create_source_group_form.validation.required'),
              })}
              {...testid('groupName')}
            />
          </div>

          <div className={st.Field}>
            <Control.Input
              id="description"
              label={t('create_source_group_form.description')}
              {...register('description')}
              {...testid('description')}
            />
          </div>

          <div className={st.Field}>
            <Select
              classNamePrefix="CreateSourceGroupDepartment"
              defaultOptions
              isAsync
              isClearable
              label={t('create_source_group_form.owner_department')}
              loadOptions={loadDepartmentOptions}
              menuPortalTarget={document.body}
              onChange={(option) => setDepartmentOption(option ?? undefined)}
              placeholder={t('create_source_group_form.owner_department')}
              styles={selectMenuPortalStyles}
              value={departmentOption}
              {...testid('ownerDepartment')}
            />
          </div>

          <div className={st.Field}>
            <Select
              classNamePrefix="CreateSourceGroupPermissionTag"
              defaultOptions
              isAsync
              isClearable
              label={t('create_source_group_form.permission_tag')}
              loadOptions={loadPermissionTagOptions}
              menuPortalTarget={document.body}
              onChange={(option) => setPermissionTagOption(option ?? undefined)}
              placeholder={t('create_source_group_form.permission_tag')}
              styles={selectMenuPortalStyles}
              value={permissionTagOption}
              {...testid('permissionTag')}
            />
          </div>

          <div className={st.Toast}>
            <InfoCircleFilledIcon className={st.ToastIcon} />
            <span className={st.ToastText}>
              {t('create_source_group_form.analytics_toast')}
            </span>
          </div>
        </div>

        <div className={st.Actions}>
          <Control.Button
            className={st.Action}
            disabled={isSubmitDisabled}
            isLoading={isPending}
            type="submit"
          >
            {t('create_source_group_form.on_submit')}
          </Control.Button>

          <Control.Button
            className={st.Action}
            disabled={isPending}
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('create_source_group_form.on_cancel')}
          </Control.Button>
        </div>
      </div>
    </form>
  );
}

CreateSourceGroupForm.displayName = displayName;

export { CreateSourceGroupForm };
